import React, { memo, useContext, useMemo } from 'react'

import { Icon, RotatingSpinner } from '@cgtrader/triangle-ui/dist/src/ssr-index'

// eslint-disable-next-line max-len
import useGetLibraryItems from 'Pods/ThreeDStudio/Components/Sidebar/Components/LibraryContent/Hooks/useGetLibraryItems'
import { VARIATION_TYPES } from 'Pods/ThreeDStudio/constants'
import useTranslation from 'Pods/ThreeDStudio/Hooks/useTranslation'
import { getAssetByTypeAndSource } from 'Pods/ThreeDStudio/Shared/Utils/getAssetByTypeAndSource'
import { useThreeDStudioStore } from 'Pods/ThreeDStudio/Store'
import ThreeDStudioPageContext from 'Pods/ThreeDStudio/ThreeDStudioPageContext'

import Card from './Components/Card/Card'
import EmptyState from './Components/EmptyState/EmptyState'
import UploadedModelCard from './Components/UploadedModelCard/UploadedModelCard'
import useGetVariations from './Hooks/useGetVariations'
import useVariationActions from './Hooks/useVariationActions'

const spinnerSize = { height: '32px', width: '32px' }

function VariationsTab() {
  const t = useTranslation()
  const pageContext = useContext(ThreeDStudioPageContext)
  const isLoggedIn = pageContext?.userLoggedIn
  const openedModel = useThreeDStudioStore(state => state.ui?.openedModel)
  const isUploadedModelLoaded = useThreeDStudioStore(state => !!state.modelSettings?.inputThreeDUrl)
  const sessionId = openedModel?.generatorSessionId

  const { variations, isLoading } = useGetVariations(sessionId)
  const { items: libraryItems } = useGetLibraryItems(isLoggedIn, '')
  const {
    assignMain,
    deleteVariation,
    isAssigning,
    isDeleting,
  } = useVariationActions(variations)

  const mainRunId = useMemo(() => {
    const session = libraryItems.find(item => String(item.id) === String(sessionId))
    const mainRun = session?.attributes?.mainRun
    return mainRun?.id
  }, [libraryItems, sessionId])

  const isEnhanced = useMemo(() =>
    variations.some(variation => VARIATION_TYPES[variation.providerModelId]?.isEnhancement),
    [variations],
  )

  const uploadedAsset = useMemo(() => (
    variations
      .map(variation => getAssetByTypeAndSource(variation.inputAssets, '3d_model', 'user_upload'))
      .find(asset => asset.fileUrl) ?? null
  ), [variations])

  const isActionLoading = isAssigning || isDeleting

  return (
    <div className="enhance-model-panel__tab">
      <span className="enhance-model-panel__title cgt-heading-50">{t('viewerSidebar.enhance.variations.title')}</span>
      {isLoading && (
        <div className="variations-tab__loading">
          <Icon svgComponent={RotatingSpinner} color="primary" size={spinnerSize} notSpaced />
        </div>
      )}
      {!isLoading && variations.length > 0 && (
        <div className="variations-tab__grid">
          {uploadedAsset && <UploadedModelCard asset={uploadedAsset} isActive={isUploadedModelLoaded} />}
          {variations.map((variation, index) => (
            <Card
              key={variation.id}
              variation={variation}
              position={index + 1}
              isActive={!isUploadedModelLoaded && String(variation.id) === String(openedModel?.id)}
              isMain={mainRunId != null && String(variation.id) === String(mainRunId)}
              isActionLoading={isActionLoading}
              onSetAsMain={assignMain}
              onDelete={deleteVariation}
            />
          ))}
        </div>
      )}
      {!isLoading && !isEnhanced && <EmptyState />}
    </div>
  )
}

export default memo(VariationsTab)
